// File: utils/storage.js

/**
 * LocalStorage Wrapper
 * 
 * Provides a safe interface for localStorage with error handling,
 * JSON serialization, and storage limit checks.
 */

import { STORAGE_KEYS, LIMITS } from './constants.js';

// ========================================
// Core Storage Functions
// ========================================

/**
 * Check if localStorage is available
 */
export function isStorageAvailable() {
  try {
    const test = '__storage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * Get item from localStorage
 */
export function getItem(key, defaultValue = null) {
  if (!isStorageAvailable()) {
    console.warn('localStorage not available');
    return defaultValue;
  }
  
  try {
    const item = localStorage.getItem(key);
    if (item === null) return defaultValue;
    
    // Try to parse as JSON, fallback to string
    try {
      return JSON.parse(item);
    } catch {
      return item;
    }
  } catch (error) {
    console.error(`Error reading from localStorage (${key}):`, error);
    return defaultValue;
  }
}

/**
 * Set item in localStorage
 */
export function setItem(key, value) {
  if (!isStorageAvailable()) {
    console.warn('localStorage not available');
    return false;
  }
  
  try {
    const stringValue = typeof value === 'string' ? value : JSON.stringify(value);
    localStorage.setItem(key, stringValue);
    return true;
  } catch (error) {
    if (error.name === 'QuotaExceededError') {
      console.error('localStorage quota exceeded');
    } else {
      console.error(`Error writing to localStorage (${key}):`, error);
    }
    return false;
  }
}

/**
 * Remove item from localStorage
 */
export function removeItem(key) {
  if (!isStorageAvailable()) {
    return false;
  }
  
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error(`Error removing from localStorage (${key}):`, error);
    return false;
  }
}

/**
 * Clear all items from localStorage
 */
export function clear() {
  if (!isStorageAvailable()) {
    return false;
  }
  
  try {
    localStorage.clear();
    return true;
  } catch (error) {
    console.error('Error clearing localStorage:', error);
    return false;
  }
}

/**
 * Get all keys in localStorage
 */
export function getAllKeys() {
  if (!isStorageAvailable()) {
    return [];
  }
  
  try {
    return Object.keys(localStorage);
  } catch (error) {
    console.error('Error getting localStorage keys:', error);
    return [];
  }
}

/**
 * Get storage usage in bytes
 */
export function getStorageSize() {
  if (!isStorageAvailable()) {
    return 0;
  }
  
  try {
    let size = 0;
    for (const key in localStorage) {
      if (localStorage.hasOwnProperty(key)) {
        size += localStorage[key].length + key.length;
      }
    }
    return size;
  } catch (error) {
    console.error('Error calculating storage size:', error);
    return 0;
  }
}

// ========================================
// Bookmarks
// ========================================

/**
 * Get all bookmarks
 */
export function getBookmarks() {
  return getItem(STORAGE_KEYS.BOOKMARKS, []);
}

/**
 * Save bookmarks
 */
export function setBookmarks(bookmarks) {
  return setItem(STORAGE_KEYS.BOOKMARKS, bookmarks);
}

/**
 * Add bookmark
 */
export function addBookmark(section) {
  const bookmarks = getBookmarks();
  
  // Check if already bookmarked
  const exists = bookmarks.some(b => b.code === section.code);
  if (exists) {
    return { success: false, message: 'Already bookmarked' };
  }
  
  // Check limit
  if (bookmarks.length >= LIMITS.MAX_BOOKMARKS) {
    return { success: false, message: `Maximum ${LIMITS.MAX_BOOKMARKS} bookmarks reached` };
  }
  
  // Add bookmark with timestamp
  const bookmark = {
    ...section,
    bookmarked_at: new Date().toISOString(),
  };
  
  bookmarks.push(bookmark);
  setBookmarks(bookmarks);
  announceAnnotationChange(section.code);

  return { success: true, bookmark };
}

// Notify listeners (account annotation sync) that a section's bookmark or
// note changed. No-op unless something is listening.
function announceAnnotationChange(code) {
  try {
    window.dispatchEvent(new CustomEvent('osha:annotation-changed', { detail: { code } }));
  } catch {
    /* non-browser environment */
  }
}

/**
 * Remove bookmark
 */
export function removeBookmark(code) {
  const bookmarks = getBookmarks();
  const filtered = bookmarks.filter(b => b.code !== code);
  setBookmarks(filtered);
  announceAnnotationChange(code);
  return { success: true };
}

/**
 * Check if section is bookmarked
 */
export function isBookmarked(code) {
  const bookmarks = getBookmarks();
  return bookmarks.some(b => b.code === code);
}

/**
 * Get bookmark by code
 */
export function getBookmark(code) {
  const bookmarks = getBookmarks();
  return bookmarks.find(b => b.code === code) || null;
}

// ========================================
// Notes
// ========================================

/**
 * Get all notes
 */
export function getNotes() {
  return getItem(STORAGE_KEYS.NOTES, []);
}

/**
 * Save notes
 */
export function setNotes(notes) {
  return setItem(STORAGE_KEYS.NOTES, notes);
}

/**
 * Add or update note
 */
export function saveNote(sectionCode, noteText) {
  const notes = getNotes();
  
  // Check limit
  if (notes.length >= LIMITS.MAX_NOTES) {
    return { success: false, message: `Maximum ${LIMITS.MAX_NOTES} notes reached` };
  }
  
  // Find existing note
  const existingIndex = notes.findIndex(n => n.section === sectionCode);
  
  const note = {
    section: sectionCode,
    text: noteText,
    created_at: existingIndex >= 0 ? notes[existingIndex].created_at : new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };
  
  if (existingIndex >= 0) {
    notes[existingIndex] = note;
  } else {
    notes.push(note);
  }

  setNotes(notes);
  announceAnnotationChange(sectionCode);
  return { success: true, note };
}

/**
 * Remove note
 */
export function removeNote(sectionCode) {
  const notes = getNotes();
  const filtered = notes.filter(n => n.section !== sectionCode);
  setNotes(filtered);
  announceAnnotationChange(sectionCode);
  return { success: true };
}

/**
 * Get note for section
 */
export function getNote(sectionCode) {
  const notes = getNotes();
  return notes.find(n => n.section === sectionCode) || null;
}

/**
 * Check if section has note
 */
export function hasNote(sectionCode) {
  return getNote(sectionCode) !== null;
}

// ========================================
// Recent Sections
// ========================================

/**
 * Get recent sections
 */
export function getRecentSections() {
  return getItem(STORAGE_KEYS.RECENT_SECTIONS, []);
}

/**
 * Add section to recent
 */
export function addRecentSection(section) {
  const recent = getRecentSections();
  
  // Remove if already exists
  const filtered = recent.filter(s => s.code !== section.code);
  
  // Add to beginning
  filtered.unshift({
    ...section,
    viewed_at: new Date().toISOString(),
  });
  
  // Keep only MAX_RECENT_SECTIONS
  const trimmed = filtered.slice(0, LIMITS.MAX_RECENT_SECTIONS);
  
  setItem(STORAGE_KEYS.RECENT_SECTIONS, trimmed);
  return { success: true };
}

/**
 * Clear recent sections
 */
export function clearRecentSections() {
  return setItem(STORAGE_KEYS.RECENT_SECTIONS, []);
}

// ========================================
// Search History
// ========================================

/**
 * Get search history
 */
export function getSearchHistory() {
  return getItem(STORAGE_KEYS.SEARCH_HISTORY, []);
}

/**
 * Add to search history
 */
export function addSearchHistory(query) {
  if (!query || query.trim().length === 0) return;
  
  const history = getSearchHistory();
  
  // Remove if already exists
  const filtered = history.filter(q => q.toLowerCase() !== query.toLowerCase());
  
  // Add to beginning
  filtered.unshift(query.trim());
  
  // Keep only MAX_SEARCH_HISTORY
  const trimmed = filtered.slice(0, LIMITS.MAX_SEARCH_HISTORY);
  
  setItem(STORAGE_KEYS.SEARCH_HISTORY, trimmed);
}

/**
 * Clear search history
 */
export function clearSearchHistory() {
  return setItem(STORAGE_KEYS.SEARCH_HISTORY, []);
}

// ========================================
// Settings
// ========================================

/**
 * Get language preference
 */
export function getLanguage() {
  return getItem(STORAGE_KEYS.LANGUAGE, 'en');
}

/**
 * Set language preference
 */
export function setLanguage(lang) {
  return setItem(STORAGE_KEYS.LANGUAGE, lang);
}

/**
 * Get navigation view preference
 */
export function getNavigationView() {
  return getItem(STORAGE_KEYS.NAVIGATION_VIEW, 'topic');
}

/**
 * Set navigation view preference
 */
export function setNavigationView(view) {
  return setItem(STORAGE_KEYS.NAVIGATION_VIEW, view);
}

/**
 * Get theme preference
 */
export function getTheme() {
  return getItem(STORAGE_KEYS.THEME, 'system');
}

/**
 * Set theme preference
 */
export function setTheme(theme) {
  return setItem(STORAGE_KEYS.THEME, theme);
}

/**
 * Get last visited section
 */
export function getLastVisited() {
  return getItem(STORAGE_KEYS.LAST_VISITED, null);
}

/**
 * Set last visited section
 */
export function setLastVisited(section) {
  return setItem(STORAGE_KEYS.LAST_VISITED, {
    ...section,
    visited_at: new Date().toISOString(),
  });
}

// ========================================
// Bulk Operations
// ========================================

/**
 * Export all user data
 */
export function exportUserData() {
  return {
    bookmarks: getBookmarks(),
    notes: getNotes(),
    recent: getRecentSections(),
    searchHistory: getSearchHistory(),
    language: getLanguage(),
    navigationView: getNavigationView(),
    theme: getTheme(),
    exported_at: new Date().toISOString(),
  };
}

/**
 * Import user data
 */
export function importUserData(data) {
  try {
    if (data.bookmarks) setBookmarks(data.bookmarks);
    if (data.notes) setNotes(data.notes);
    if (data.recent) setItem(STORAGE_KEYS.RECENT_SECTIONS, data.recent);
    if (data.searchHistory) setItem(STORAGE_KEYS.SEARCH_HISTORY, data.searchHistory);
    if (data.language) setLanguage(data.language);
    if (data.navigationView) setNavigationView(data.navigationView);
    if (data.theme) setTheme(data.theme);
    
    return { success: true };
  } catch (error) {
    console.error('Error importing user data:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Clear all user data
 */
export function clearUserData() {
  setBookmarks([]);
  setNotes([]);
  clearRecentSections();
  clearSearchHistory();
  return { success: true };
}

// ========================================
// Statistics
// ========================================

/**
 * Get storage statistics
 */
export function getStorageStats() {
  return {
    bookmarks: getBookmarks().length,
    notes: getNotes().length,
    recent: getRecentSections().length,
    searchHistory: getSearchHistory().length,
    totalSize: getStorageSize(),
    isAvailable: isStorageAvailable(),
  };
}

/**
 * Get all user settings
 * @returns {Object} Object containing all settings
 */
export const getSettings = () => {
    return {
        language: getLanguage(),
        navigationView: getNavigationView(),
        theme: getTheme(),
        lastViewed: getLastVisited(),
        lastViewedRegulation: getItem(STORAGE_KEYS.LAST_VISITED)?.regulationCode || '1910'
    };
};


// ========================================
// Export All
// ========================================

const storage = {
  // Core
  isStorageAvailable,
  getItem,
  setItem,
  removeItem,
  clear,
  getAllKeys,
  getStorageSize,
  
  // Bookmarks
  getBookmarks,
  setBookmarks,
  addBookmark,
  removeBookmark,
  isBookmarked,
  getBookmark,
  
  // Notes
  getNotes,
  setNotes,
  saveNote,
  removeNote,
  getNote,
  hasNote,
  
  // Recent
  getRecentSections,
  addRecentSection,
  clearRecentSections,
  
  // Search History
  getSearchHistory,
  addSearchHistory,
  clearSearchHistory,
  
  // Settings
  getLanguage,
  setLanguage,
  getNavigationView,
  setNavigationView,
  getTheme,
  setTheme,
  getLastVisited,
  setLastVisited,
  
  // Bulk
  exportUserData,
  importUserData,
  clearUserData,
  
  // Stats
  getStorageStats,
};

export default storage;
